import React, { useState, useRef } from 'react';
import { 
  TicketPreview, 
  FlightForm, 
  WalletButton, 
  MintButton,
  Gallery,
  ToastContainer,
  useToast 
} from './components';
import { useWallet, useMint, MINT_STAGES } from './hooks';
import './styles/globals.css';

const DEFAULT_FLIGHT_DATA = {
  flightNumber: 'UA 328',
  departure: 'EWR',
  arrival: 'SFO',
  date: new Date().toISOString().split('T')[0],
  departureTime: '08:00',
  arrivalTime: '11:25',
  aircraft: 'Boeing 787-10',
  tailNumber: 'N12010',
  seat: '1A',
  classOfService: 'Polaris Business',
  seatConfiguration: '1-2-1 Polaris, 2-4-2 Premium Plus, 3-3-3 Economy',
  configAutoDetected: false,
  captain: '',
  firstOfficer: '',
  cabinCrew: '',
  duration: '5h 25m',
  departureGate: '',
  arrivalGate: '',
  lounges: {
    departurePolarisLounge: false,
    departureUnitedClub: false,
    arrivalPolarisLounge: false,
    arrivalUnitedClub: false,
  },
  // Auto-detection flags for FlightAware-populated fields
  departureAutoDetected: false,
  arrivalAutoDetected: false,
  departureTimeAutoDetected: false,
  arrivalTimeAutoDetected: false,
  durationAutoDetected: false,
  tailNumberAutoDetected: false,
  departureGateAutoDetected: false,
  arrivalGateAutoDetected: false,
  aircraftAutoDetected: false,
};

// Navigation Tab Component
const NavTabs = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'mint', label: 'Mint', icon: '✈️' },
    { id: 'gallery', label: 'Gallery', icon: '🖼️' },
  ];

  return (
    <div className="nav-tabs" style={{
      display: 'flex',
      gap: '4px',
      background: 'rgba(255,255,255,0.05)',
      padding: '4px',
      borderRadius: '10px',
    }}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className="nav-tabs-button"
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === tab.id 
              ? 'linear-gradient(135deg, #0033A0 0%, #004494 100%)' 
              : 'transparent',
            color: activeTab === tab.id ? '#ffffff' : 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
        >
          <span>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// Mint View Component
const MintView = ({ 
  flightData, 
  setFlightData, 
  ticketRef, 
  stage, 
  error, 
  txHash, 
  tokenId, 
  connected, 
  isOnBase, 
  onMint, 
  onReset 
}) => (
  <main style={{
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    alignItems: 'start',
    overflow: 'hidden',
  }} className="main-grid">
    
    {/* Left Column - Form */}
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '28px',
    }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <span style={{ fontSize: '20px' }}>🎫</span>
        Flight Details
      </h2>
      
      <FlightForm 
        flightData={flightData} 
        onChange={setFlightData} 
      />
    </div>

    {/* Right Column - Preview & Mint */}
    <div style={{ overflow: 'hidden' }}>
      {/* Preview Container */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.08)',
        padding: '28px',
        marginBottom: '24px',
        overflow: 'hidden',
      }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '20px' }}>👁️</span>
          NFT Preview
        </h2>

        {/* Ticket Preview */}
        <div 
          className="ticket-preview-container"
          style={{
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <TicketPreview ref={ticketRef} flightData={flightData} />
        </div>
      </div>

      {/* Mint Button */}
      <MintButton
        stage={stage}
        connected={connected}
        isOnBase={isOnBase}
        txHash={txHash}
        tokenId={tokenId}
        error={error}
        onMint={onMint}
        onReset={onReset}
        flightData={flightData}
        ticketRef={ticketRef}
      />
    </div>
  </main>
);

// Gallery View Component
const GalleryView = ({ connectedAccount }) => (
  <main style={{
    maxWidth: '1400px',
    margin: '0 auto',
  }}>
    <Gallery connectedAccount={connectedAccount} />
  </main>
);

function App() {
  const [activeTab, setActiveTab] = useState('mint');
  const [flightData, setFlightData] = useState(DEFAULT_FLIGHT_DATA);
  const ticketRef = useRef(null);
  const { toasts, removeToast, success, error: toastError } = useToast();

  // Wallet hook
  const { 
    account, 
    connected, 
    connecting, 
    provider, 
    isOnBase, 
    connect, 
    switchToBase 
  } = useWallet();

  // Minting hook
  const { 
    stage, 
    error, 
    txHash, 
    tokenId, 
    mint, 
    reset 
  } = useMint(provider);

  // Handle minting
  const handleMint = async () => {
    if (!ticketRef.current) return;

    // Ensure we're on Base
    if (!isOnBase) {
      const switched = await switchToBase();
      if (!switched) {
        toastError('Please switch to Base network');
        return;
      }
    }

    const result = await mint(ticketRef.current, flightData);
    
    if (result) {
      success('NFT minted successfully! 🎉');
    }
  };

  // Handle wallet connection
  const handleConnect = async () => {
    const result = await connect();
    if (result) {
      success('Wallet connected');
      if (!isOnBase) {
        await switchToBase();
      }
    }
  };

  return (
    <div style={{ minHeight: '100vh', padding: '24px' }} className="app-container">
      {/* Header */}
      <header className="app-header" style={{
        maxWidth: '1400px',
        margin: '0 auto 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* United Logo */}
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0033A0 0%, #004494 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 30px rgba(0, 51, 160, 0.4)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" opacity="0.9"/>
              <path d="M12 2 C12 2 16 8 16 12 C16 16 12 22 12 22 C12 22 8 16 8 12 C8 8 12 2 12 2" stroke="white" strokeWidth="1.5" fill="none"/>
              <ellipse cx="12" cy="12" rx="10" ry="4" stroke="white" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#ffffff',
              letterSpacing: '-0.5px',
            }}>
              United Flight NFTs
            </h1>
            <p style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.5)',
              marginTop: '2px',
            }}>
              Mint collectible flight segment NFTs on Base
            </p>
          </div>
        </div>

        {/* Center - Navigation Tabs */}
        <NavTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Right - Wallet */}
        <WalletButton
          connected={connected}
          connecting={connecting}
          account={account}
          isOnBase={isOnBase}
          onConnect={handleConnect}
          onSwitchNetwork={switchToBase}
        />
      </header>

      {/* Main Content - Switch based on active tab */}
      {activeTab === 'mint' && (
        <MintView
          flightData={flightData}
          setFlightData={setFlightData}
          ticketRef={ticketRef}
          stage={stage}
          error={error}
          txHash={txHash}
          tokenId={tokenId}
          connected={connected}
          isOnBase={isOnBase}
          onMint={handleMint}
          onReset={reset}
        />
      )}

      {activeTab === 'gallery' && (
        <GalleryView connectedAccount={account} />
      )}

      {/* Footer */}
      <footer style={{
        maxWidth: '1400px',
        margin: '60px auto 0',
        padding: '24px 0',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '13px',
        color: 'rgba(255,255,255,0.4)',
      }}>
        <div>
          Built with ❤️ for aviation enthusiasts
        </div>
        <div style={{ display: 'flex', gap: '24px' }}>
          <a 
            href="https://basescan.org/address/0xF474DdD8fc0CAdA758F92c37aB6A565eC9e63935" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Contract
          </a>
          <a 
            href="https://opensea.io/collection/united-flight-segment" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            OpenSea
          </a>
        </div>
      </footer>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;
